// Beach day scoring algorithm
// Calculates a 0-10 score based on weather and ocean conditions

// Score temperature (25% weight)
const scoreTemperature = (temp, minPref, maxPref) => {
  if (temp < minPref || temp > maxPref) {
    const distance = temp < minPref ? minPref - temp : temp - maxPref;
    const penalty = Math.min(distance / 10, 1);
    return Math.max(0, 10 - (penalty * 10));
  }
  return 10;
};

// Score wind speed (15% weight for weather-only, 12% with ocean)
const scoreWindSpeed = (windSpeed, maxPref) => {
  if (windSpeed <= maxPref) return 10;
  const excess = windSpeed - maxPref;
  return Math.max(0, 10 - (excess / 2));
};

// Score precipitation probability (15% weight)
const scorePrecipitation = (precipProb, maxPref) => {
  if (precipProb <= maxPref) return 10;
  const excess = precipProb - maxPref;
  return Math.max(0, 10 - (excess / 5));
};

// Score cloud cover (10% weight)
const scoreCloudCover = (cloudCover, maxPref) => {
  if (cloudCover <= maxPref) return 10;
  const excess = cloudCover - maxPref;
  return Math.max(0, 10 - (excess / 7));
};

// Score weather code (10% weight for weather-only, 8% with ocean)
const scoreWeatherCode = (weatherCode) => {
  if (weatherCode === 0) return 10; // Clear
  if (weatherCode <= 2) return 9;   // Mainly clear/partly cloudy
  if (weatherCode <= 3) return 7;   // Overcast
  if (weatherCode <= 48) return 4;  // Fog
  if (weatherCode <= 55) return 3;  // Drizzle
  if (weatherCode <= 65) return 2;  // Rain
  return 0; // Heavy rain/snow/thunderstorm
};

// Score humidity (5% weight)
const scoreHumidity = (humidity) => {
  if (humidity <= 60) return 10;
  if (humidity <= 70) return 8;
  if (humidity <= 80) return 6;
  return 4;
};

// Score ocean conditions (30% weight when available)
// Waves weighted more heavily (65%) than water temp (35%) since waves change daily
const scoreOceanConditions = (dayData, userPreferences) => {
  let waveScore = 10;
  let waterTempScore = 10;

  // Wave height preference (65% of ocean score)
  if (dayData.waveHeight !== null && dayData.waveHeight !== undefined) {
    waveScore = userPreferences.wavePreference === 'calm' ?
      Math.max(0, 10 - dayData.waveHeight * 2) : // Calm: penalize waves
      userPreferences.wavePreference === 'moderate' ?
      (dayData.waveHeight >= 2 && dayData.waveHeight <= 4 ? 10 : 5) : // Moderate: 2-4ft ideal
      (dayData.waveHeight >= 4 ? 10 : 5); // Active: bigger is better
  }

  // Water temperature (35% of ocean score)
  if (dayData.waterTemp !== null && dayData.waterTemp !== undefined) {
    waterTempScore = dayData.waterTemp >= userPreferences.minWaterTemp ? 10 :
      Math.max(0, 10 - (userPreferences.minWaterTemp - dayData.waterTemp) * 0.5);
  }

  // Combined ocean score: 65% waves, 35% water temp
  const score = waveScore * 0.65 + waterTempScore * 0.35;

  return { score, waveScore, waterTempScore };
};

// Premium: Calculate custom weights based on user priorities
const getCustomWeights = (priorities, hasOceanData) => {
  // Default weights (no priorities set)
  const defaultWeights = hasOceanData ? {
    airTemp: 0.20,
    wind: 0.12,
    precip: 0.12,
    cloud: 0.08,
    weatherCode: 0.08,
    humidity: 0.04,
    waterTemp: 0.10,
    waves: 0.20
  } : {
    airTemp: 0.25,
    wind: 0.20,
    precip: 0.20,
    cloud: 0.15,
    weatherCode: 0.10,
    humidity: 0.10
  };

  if (!priorities || priorities.length === 0) {
    return defaultWeights;
  }

  // Map priority names to factor names
  const factorMap = {
    'airTemp': ['airTemp'],
    'waterTemp': ['waterTemp'],
    'waves': ['waves'],
    'wind': ['wind'],
    'rain': ['precip'],
    'clouds': ['cloud', 'weatherCode']
  };

  // Start with base weights (everyone gets something)
  const weights = Object.keys(defaultWeights).reduce((acc, key) => {
    acc[key] = 0.05; // 5% baseline for each factor
    return acc;
  }, {});

  // Calculate how much weight we have left to distribute
  const baselineTotal = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const remainingWeight = 1.0 - baselineTotal;

  // Priority weights: 1st = 40%, 2nd = 35%, 3rd = 25% of remaining weight
  const priorityWeights = [0.40, 0.35, 0.25];

  // Distribute priority weights
  priorities.forEach((priority, index) => {
    if (index >= 3) return; // Only top 3 priorities

    const factors = factorMap[priority] || [];
    const weightToAdd = remainingWeight * priorityWeights[index];
    const weightPerFactor = weightToAdd / factors.length;

    factors.forEach(factor => {
      if (weights[factor] !== undefined) {
        weights[factor] += weightPerFactor;
      }
    });
  });

  return weights;
};

// Main scoring function - works for full day or time period (morning/afternoon)
export const calculateBeachScore = (dayData, userPreferences, hasOceanData = false) => {
  const temp = dayData.tempAvg !== undefined ? dayData.tempAvg : (dayData.tempMax + dayData.tempMin) / 2;
  const windScore = scoreWindSpeed(dayData.windSpeed, userPreferences.maxWindSpeed || 10);
  const precipScore = scorePrecipitation(dayData.precipProb, userPreferences.maxPrecipChance || 10);
  const cloudScore = scoreCloudCover(dayData.cloudCover, userPreferences.maxCloudCover || 30);
  const weatherCodeScore = scoreWeatherCode(dayData.weatherCode);
  const humidityScore = scoreHumidity(dayData.humidity || 50);
  const tempScore = scoreTemperature(temp, userPreferences.tempMin || 75, userPreferences.tempMax || 85);

  let totalScore;
  let breakdown;

  // Check if user has custom priorities (Premium feature)
  const usePriorities = userPreferences.isPremium && userPreferences.scoringPriorities;

  if (hasOceanData && (dayData.waveHeight !== null || dayData.waterTemp !== null)) {
    const oceanData = scoreOceanConditions(dayData, userPreferences);

    if (usePriorities) {
      // Custom weights based on user priorities
      const weights = getCustomWeights(userPreferences.scoringPriorities, true);
      totalScore = (
        tempScore * weights.airTemp +
        windScore * weights.wind +
        precipScore * weights.precip +
        cloudScore * weights.cloud +
        weatherCodeScore * weights.weatherCode +
        humidityScore * weights.humidity +
        oceanData.waveScore * weights.waves +
        oceanData.waterTempScore * weights.waterTemp
      );
    } else {
      // Default weights
      totalScore = (
        tempScore * 0.20 +      // 20% temperature
        windScore * 0.12 +      // 12% wind
        precipScore * 0.12 +    // 12% precipitation
        cloudScore * 0.08 +     // 8% clouds
        weatherCodeScore * 0.08 + // 8% weather code
        humidityScore * 0.04 +  // 4% humidity
        oceanData.score * 0.30  // 30% ocean conditions (combined)
      );
    }

    breakdown = {
      temp: tempScore,
      wind: windScore,
      precip: precipScore,
      cloud: cloudScore,
      weatherCode: weatherCodeScore,
      humidity: humidityScore,
      ocean: oceanData.score,
      waves: oceanData.waveScore,
      waterTemp: oceanData.waterTempScore
    };
  } else {
    if (usePriorities) {
      // Custom weights for non-ocean locations
      const weights = getCustomWeights(userPreferences.scoringPriorities, false);
      totalScore = (
        tempScore * weights.airTemp +
        windScore * weights.wind +
        precipScore * weights.precip +
        cloudScore * weights.cloud +
        weatherCodeScore * weights.weatherCode +
        humidityScore * weights.humidity
      );
    } else {
      // Default weights
      totalScore = (
        tempScore * 0.25 +      // 25% temperature
        windScore * 0.15 +      // 15% wind
        precipScore * 0.15 +    // 15% precipitation
        cloudScore * 0.10 +     // 10% clouds
        weatherCodeScore * 0.10 + // 10% weather code
        humidityScore * 0.05 +  // 5% humidity
        windScore * 0.10 +      // 10% wind again (replaces ocean weight)
        cloudScore * 0.10       // 10% clouds again
      );
    }

    breakdown = { temp: tempScore, wind: windScore, precip: precipScore, cloud: cloudScore, weatherCode: weatherCodeScore, humidity: humidityScore };
  }

  return {
    score: Math.round(totalScore * 10) / 10,
    breakdown,
    hasOceanData
  };
};

// Calculate scores for morning and afternoon periods
export const calculateTimePeriodScores = (dayData, userPreferences, hasOceanData = false) => {
  return {
    morning: dayData.morning ? calculateBeachScore(dayData.morning, userPreferences, hasOceanData) : null,
    afternoon: dayData.afternoon ? calculateBeachScore(dayData.afternoon, userPreferences, hasOceanData) : null,
    fullDay: calculateBeachScore(dayData, userPreferences, hasOceanData)
  };
};

// Get color class based on score
export const getScoreColor = (score) => {
  if (score >= 9) return 'text-green-600';
  if (score >= 7) return 'text-yellow-600';
  if (score >= 5) return 'text-orange-500';
  return 'text-red-500';
};

// Get emoji based on score
export const getScoreEmoji = (score) => {
  if (score >= 9.5) return '🏆';
  if (score >= 9) return '😎';
  if (score >= 7) return '😊';
  if (score >= 5) return '😐';
  return '😞';
};

// Explain why the score is what it is
export const explainScore = (scoreData, dayData, userPreferences) => {
  const { breakdown, score } = scoreData;
  const issues = [];
  const highlights = [];

  // Check temperature
  if (breakdown.temp < 7) {
    const temp = Math.round(dayData.tempMax);
    if (temp < userPreferences.tempMin) {
      issues.push(`cooler than you prefer (${temp}°F)`);
    } else if (temp > userPreferences.tempMax) {
      issues.push(`hotter than you prefer (${temp}°F)`);
    }
  } else if (breakdown.temp >= 9) {
    highlights.push('perfect temperature');
  }

  // Check wind
  if (breakdown.wind < 7) {
    issues.push(`windy (${Math.round(dayData.windSpeed)}mph)`);
  } else if (breakdown.wind >= 9) {
    highlights.push('calm winds');
  }

  // Check rain
  if (breakdown.precip < 7) {
    issues.push(`${dayData.precipProb}% chance of rain`);
  } else if (breakdown.precip >= 9 && dayData.precipProb === 0) {
    highlights.push('no rain');
  }

  // Check clouds
  if (breakdown.cloud < 7) {
    const clouds = Math.round(dayData.cloudCover);
    if (clouds > 60) {
      issues.push(`mostly cloudy (${clouds}%)`);
    } else {
      issues.push(`${clouds}% cloud cover`);
    }
  } else if (breakdown.cloud >= 9) {
    highlights.push('clear skies');
  }

  // Check water temperature (if available)
  if (breakdown.waterTemp !== undefined && breakdown.waterTemp < 7) {
    const waterTemp = Math.round(dayData.waterTemp);
    issues.push(`chilly water (${waterTemp}°F)`);
  }

  // Check waves (if available)
  if (breakdown.waves !== undefined && breakdown.waves < 7) {
    const waves = dayData.waveHeight ? dayData.waveHeight.toFixed(1) : 'unknown';
    if (userPreferences.wavePreference === 'calm') {
      issues.push(`choppy waves (${waves} ft)`);
    }
  }

  // Generate explanation based on score
  if (score >= 9.5) {
    return '🏆 Absolutely perfect conditions! Drop everything and go!';
  } else if (score >= 9) {
    if (highlights.length > 0) {
      return `Nearly perfect! ${highlights.slice(0, 2).join(', ')}.`;
    }
    return 'Nearly perfect beach day!';
  } else if (score >= 7) {
    if (issues.length === 0) {
      return 'Great beach day overall!';
    } else if (issues.length === 1) {
      return `Pretty good day, though ${issues[0]}.`;
    } else {
      return `Pretty good, but ${issues[0]} and ${issues[1]}.`;
    }
  } else if (score >= 5) {
    if (issues.length === 0) {
      return 'Decent conditions, but not ideal.';
    } else if (issues.length === 1) {
      return `Okay day, though ${issues[0]}.`;
    } else {
      return `Score lowered by ${issues.slice(0, 2).join(' and ')}.`;
    }
  } else {
    // Low score - focus on main issues
    if (issues.length === 0) {
      return 'Not the best beach day.';
    } else if (issues.length === 1) {
      return `Not ideal - ${issues[0]}.`;
    } else if (issues.length === 2) {
      return `Challenging conditions: ${issues[0]} and ${issues[1]}.`;
    } else {
      return `Multiple issues: ${issues.slice(0, 2).join(', ')}, and more.`;
    }
  }
};
